import React, { useEffect, useState } from "react";
import {
  Table, Thead, Tbody, Tr, Th, Td, Box, Spinner, Flex, Text, Badge, Button, Input,
  IconButton, Tooltip, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
} from "@chakra-ui/react";
import { FaPen, FaCheck, FaTimes, FaMagic } from "react-icons/fa";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const InconsistentDescriptions = ({ onCountChange }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { ri, di }
  const [editValue, setEditValue] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const cancelRef = React.useRef();
  const toast = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/validation/inconsistent-descriptions`, {});
      const rows = res.data?.data || [];
      setData(rows);
      if (onCountChange) onCountChange(rows.length);
    } catch (err) {
      toast({ title: "Failed to load data", status: "error", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoFix = async () => {
    setConfirmOpen(false);
    setFixing(true);
    try {
      const res = await axios.post(`${API}/validation/inconsistent-descriptions/auto-fix`, {});
      const results = res.data?.data || {};
      const total = Object.values(results).reduce((s, v) => s + v, 0);
      const detail = Object.entries(results).filter(([, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast({
        title: `Auto-fix complete — ${total} rows updated`,
        description: detail || "No changes needed",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      fetchData();
    } catch (err) {
      toast({ title: "Auto-fix failed", description: err?.response?.data?.message || "Error", status: "error", duration: 3000, isClosable: true });
    } finally {
      setFixing(false);
    }
  };

  const startEdit = (ri, di, currentDesc) => {
    setEditingCell({ ri, di });
    setEditValue(currentDesc);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const saveEdit = async (row, di) => {
    const newDesc = editValue.trim();
    const oldDesc = row.descriptions[di];
    if (!newDesc || newDesc === oldDesc) {
      cancelEdit();
      return;
    }
    try {
      await axios.post(`${API}/validation/inconsistent-descriptions/update`, {
        retailer: row.retailer,
        std_site_code: row.std_site_code,
        old_description: oldDesc,
        new_description: newDesc,
      });
      toast({ title: "Updated", description: `"${oldDesc}" → "${newDesc}"`, status: "success", duration: 3000, isClosable: true });
      setEditingCell(null);
      fetchData();
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.message || "Error", status: "error", duration: 3000, isClosable: true });
    }
  };

  if (loading) return <Flex justify="center" p={4}><Spinner size="md" /></Flex>;
  if (data.length === 0) return <Text fontSize="sm" color="gray.500">No inconsistent site descriptions found.</Text>;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="xs" color="gray.500">
          Stores with multiple site_description values in sales data. Click a description to edit, or auto-fix all to use the most recent.
        </Text>
        <Button
          size="sm"
          leftIcon={<FaMagic />}
          colorScheme="purple"
          onClick={() => setConfirmOpen(true)}
          isLoading={fixing}
          loadingText="Fixing..."
        >
          Auto-fix All ({data.length})
        </Button>
      </Flex>

      <Box maxH="500px" overflowY="auto">
        <Table size="sm" variant="simple" border="1px" borderColor="gray.200">
          <Thead>
            <Tr bg="gray.50">
              <Th fontSize="xs">Site Code</Th>
              <Th fontSize="xs">Retailer</Th>
              <Th fontSize="xs">Variants</Th>
              <Th fontSize="xs">Descriptions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map((row, ri) => (
              <Tr key={ri}>
                <Td fontSize="xs" fontWeight="600">{row.std_site_code}</Td>
                <Td fontSize="xs">{row.retailer}</Td>
                <Td fontSize="xs"><Badge colorScheme="orange">{row.cnt}</Badge></Td>
                <Td fontSize="xs">
                  {row.descriptions.map((d, di) => {
                    const isEditing = editingCell?.ri === ri && editingCell?.di === di;
                    return (
                      <Flex key={di} align="center" gap={1} mb={di < row.descriptions.length - 1 ? 1 : 0}>
                        {isEditing ? (
                          <>
                            <Input size="xs" value={editValue} onChange={(e) => setEditValue(e.target.value)} flex={1}
                              onKeyDown={(e) => { if (e.key === "Enter") saveEdit(row, di); if (e.key === "Escape") cancelEdit(); }}
                              autoFocus
                            />
                            <IconButton icon={<FaCheck />} size="xs" colorScheme="green" variant="ghost" onClick={() => saveEdit(row, di)} aria-label="Save" />
                            <IconButton icon={<FaTimes />} size="xs" variant="ghost" onClick={cancelEdit} aria-label="Cancel" />
                          </>
                        ) : (
                          <>
                            <Text fontSize="xs" color={di === 0 ? "gray.700" : "red.600"} flex={1}>{d}</Text>
                            <Tooltip label="Edit this description">
                              <IconButton icon={<FaPen />} size="xs" variant="ghost" colorScheme="blue" onClick={() => startEdit(ri, di, d)} aria-label="Edit" />
                            </Tooltip>
                          </>
                        )}
                      </Flex>
                    );
                  })}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <AlertDialog isOpen={confirmOpen} leastDestructiveRef={cancelRef} onClose={() => setConfirmOpen(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Auto-fix All Descriptions</AlertDialogHeader>
            <AlertDialogBody>
              This will update {data.length} stores across all retailer sales tables to use the most recent site description.
              This cannot be undone. Continue?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button colorScheme="purple" onClick={handleAutoFix} ml={3}>Fix All</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default InconsistentDescriptions;
